import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { QaIdentifier, useQa } from 'hooks/useQa';

import { ReactComponent as IconCross } from 'media/svg/cross.svg';

import styles from './Textarea.module.scss';

export interface TextareaOptions {
    name: string;
    id: string;
    value: string;
    placeholder?: string;
    maxLength?: number;
    rows?: number;
}

export interface TextareaProps {
    textarea: TextareaOptions;
    label?: {
        title: string;
    };
    withClearButton?: boolean;
    customClass?: {
        TextareaWrapper?: string;
        Textarea?: string;
        Label?: string;
    };
    hasError?: boolean;
    errorText?: string;
    isDisabled?: boolean;
    onChange: (value: string) => void;
    onClear?: () => void;
    onKeyUp?: (e: React.KeyboardEvent<HTMLElement>) => void;
    onClick?: () => void;
    qaId?: QaIdentifier;
}

const Textarea: React.FC<React.PropsWithChildren<TextareaProps>> = ({
    textarea,
    label,
    withClearButton = false,
    hasError = false,
    errorText = '',
    isDisabled = false,
    children,
    customClass,
    onChange,
    onClear,
    onKeyUp,
    onClick,
    qaId,
}) => {
    const [clearVisible, setClearVisible] = useState(false);
    const [currentPlaceholder, setCurrentPlaceholder] = useState(
        textarea.placeholder || ''
    );
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const qaAttr = useQa(qaId);
    const clearInput = () => {
        if (clearVisible) {
            onChange('');
            setClearVisible(false);
            onClear?.();
        }
    };
    const showClear = () => {
        !!textarea.value && setClearVisible(true);
    };
    const hideClear = () => {
        !textarea.value && setClearVisible(false);
    };

    const updateTextareaValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
        if (withClearButton) {
            setClearVisible(e.target.value.length > 0);
        }
    };

    const handleFocus = (_e: React.FocusEvent<HTMLTextAreaElement>) => {
        showClear();
        setCurrentPlaceholder('');

        setTimeout(() => {
            if (textAreaRef.current) {
                const len = textAreaRef.current.value.length;
                textAreaRef.current.setSelectionRange(len, len);
            }
        }, 0);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        hideClear();
        if (!e.target.value.trim()) {
            setCurrentPlaceholder(textarea.placeholder || '');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    useEffect(() => {
        if (textarea.value === '') {
            setCurrentPlaceholder(textarea.placeholder || '');
        }
    }, [textarea.value, textarea.placeholder]);

    return (
        <div
            className={classNames(
                styles.TextareaWrapper,
                customClass?.TextareaWrapper
            )}
            onClick={onClick}
            {...qaAttr}
        >
            {label && (
                <label
                    className={customClass?.Label}
                    htmlFor={textarea.id}
                    title={label.title}
                >
                    {children}
                </label>
            )}
            <textarea
                className={classNames(
                    styles.TextareaField,
                    {
                        [styles.Error]: hasError,
                        [styles.WithClear]: clearVisible,
                        [styles.Disabled]: isDisabled,
                    },
                    customClass?.Textarea
                )}
                id={textarea.id}
                value={textarea.value}
                disabled={isDisabled}
                onChange={updateTextareaValue}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={currentPlaceholder}
                onKeyDown={handleKeyDown}
                onKeyUp={onKeyUp}
                maxLength={textarea.maxLength || 500}
                rows={textarea.rows || 4}
                spellCheck={false}
                ref={textAreaRef}
            />
            {withClearButton && (
                <span
                    className={classNames(styles.ClearButton, {
                        [styles.Visible]: clearVisible,
                    })}
                    onClick={clearInput}
                >
                    <IconCross className={styles.IconCross} />
                </span>
            )}
            {!!errorText && <div className={styles.ErrorText}>{errorText}</div>}
        </div>
    );
};

export default Textarea;
